/**
 * Escolher a minha foto (docs/spec/friends.md, "Perfil"): o mesmo seletor da capa, com o Desk Cloud,
 * o computador e o endereço da web. Venha de onde vier, a imagem vira a foto do jeito de sempre:
 * recortada em quadrado e reduzida no navegador (`shrinkAvatar`) e gravada como foto da conta
 * (`PUT /api/me/avatar`). A do computador não passa pelo Desk Cloud. Configurações → Perfil e o meu
 * perfil usam o mesmo.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { useT } from '../i18n/index.js';
import { fetchDroppedImage } from '../room/droppedImage.js';
import { deskFileRefId } from '../shared.js';
import { useApp } from '../store.js';
import { ImagePickerDialog } from '../ui/ImagePicker.js';
import { toast } from '../ui/Toaster.js';
import { AVATAR_MAX_SIDE, shrinkAvatar } from './Avatar.js';

/** As imagens do Desk Cloud que servem: as que o navegador abre, até o tamanho que a busca da web aceita. */
const RULES = { types: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'], maxBytes: 25 * 1024 * 1024, maxSide: AVATAR_MAX_SIDE };

/** `open()` abre o seletor; `dialog` vai junto, em qualquer lugar da tela; `busy` enquanto grava. */
export function useAvatarPicker(): { busy: boolean; open: () => void; dialog: ReactNode } {
  const t = useT();
  const setAvatar = useApp((s) => s.setAvatar);
  const token = useApp((s) => s.token);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  /** Grava a imagem que `load` trouxer; null (não é imagem) ou erro vira o aviso de imagem inválida. */
  const save = async (load: () => Promise<Blob | null>) => {
    setBusy(true);
    try {
      const image = await load();
      if (!image) throw new Error('não é imagem');
      if (await setAvatar(await shrinkAvatar(image))) toast(t('config.profile.avatarSaved'), 'success');
    } catch {
      toast(t('config.profile.avatarInvalid'), 'error');
    } finally {
      setBusy(false);
    }
  };

  /** O arquivo do Desk Cloud vem pelo link dele; o endereço da web, pela cópia que o servidor busca. */
  const fromPick = (value: string) =>
    save(async () => {
      const fileId = deskFileRefId(value);
      const url = fileId && token ? (await api.fileUrl(token, fileId)).url : value;
      return fetchDroppedImage(url, token);
    });

  const dialog = picking ? (
    <ImagePickerDialog
      cloud
      linkRoom={false}
      rules={RULES}
      title={t('profile.avatar.title')}
      uploadHint={t('profile.avatar.uploadHint')}
      onCancel={() => setPicking(false)}
      onFile={(file) => {
        setPicking(false);
        void save(async () => file);
      }}
      onPick={(value) => {
        setPicking(false);
        void fromPick(value);
      }}
    />
  ) : null;
  return { busy, open: () => setPicking(true), dialog };
}
